import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../contexts/ThemeContext';
import { workService } from '../services/workService';
import { Task, TaskStatus } from '../types/work';
import { WorkStatusBadge } from '../components/work/WorkStatusBadge';
import { PriorityChip } from '../components/work/PriorityChip';
import { DueDateLabel } from '../components/work/DueDateLabel';
import UserBadge from '../components/common/UserBadge';
import { KanbanBoard } from '../components/work/KanbanBoard';
import { GanttChart } from '../components/work/GanttChart';
import { FiGrid, FiList, FiBarChart2, FiPlus } from 'react-icons/fi';
import CreateWorkItemModal from '../components/work/CreateWorkItemModal';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [taskView, setTaskView] = useState<'kanban' | 'table' | 'gantt'>('kanban');
  const [showCreateTask, setShowCreateTask] = useState(false);

  const { data: project, isLoading } = useQuery(
    ['project', projectId],
    () => workService.getProject(projectId!),
    { enabled: !!projectId },
  );

  const { data: tasks = [] } = useQuery(
    ['project-tasks', projectId],
    () => workService.getTasks({ project_id: projectId }),
    { enabled: !!projectId },
  );

  // Optimistic status change for Kanban drag
  const statusMutation = useMutation(
    ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      workService.updateTask(taskId, { status }),
    {
      onMutate: async ({ taskId, status }) => {
        await queryClient.cancelQueries(['project-tasks', projectId]);
        const prev = queryClient.getQueryData<Task[]>(['project-tasks', projectId]);
        queryClient.setQueryData(['project-tasks', projectId],
          prev?.map(t => t.id === taskId ? { ...t, status } : t)
        );
        return { prev };
      },
      onError: (_err, _vars, context) => {
        queryClient.setQueryData(['project-tasks', projectId], context?.prev);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['project-tasks']);
      },
    },
  );

  const handleStatusChange = useCallback((taskId: string, newStatus: TaskStatus) => {
    statusMutation.mutate({ taskId, status: newStatus });
  }, [statusMutation]);

  if (isLoading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full" /></div>;
  if (!project) return <div className="py-20 text-center text-gray-400">Project not found</div>;

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <div className={`border-l-4 border-l-teal-500 rounded-r-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
        <div className="flex-1">
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{project.title}</h1>
          {project.description && <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{project.description}</p>}
          <div className="flex items-center gap-3 mt-3">
            <WorkStatusBadge status={project.status} size="md" />
            <PriorityChip priority={project.priority} />
            <DueDateLabel date={project.due_date} />
            <UserBadge owner={project.owner} size="sm" />
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {showCreateTask && (
        <CreateWorkItemModal
          entityType="task"
          onClose={() => setShowCreateTask(false)}
          defaults={{ project_id: projectId!, goal_id: project?.goal_id || undefined }}
        />
      )}

      {/* Task view toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tasks</h2>
          <button onClick={() => setShowCreateTask(true)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-violet-600 text-white hover:bg-violet-500 transition-colors">
            <FiPlus className="w-3 h-3" /> New Task
          </button>
        </div>
        <div className={`flex rounded-lg overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-300'}`}>
          {([
            { id: 'kanban' as const, icon: FiGrid, label: 'Kanban' },
            { id: 'gantt' as const, icon: FiBarChart2, label: 'Gantt' },
            { id: 'table' as const, icon: FiList, label: 'Table' },
          ]).map(v => (
            <button
              key={v.id}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${taskView === v.id ? (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800') : (darkMode ? 'text-gray-500' : 'text-gray-400')}`}
              onClick={() => setTaskView(v.id)}
            >
              <v.icon className="w-3.5 h-3.5" /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Task content */}
      {taskView === 'gantt' ? (
        <GanttChart
          tasks={tasks}
          onTaskClick={(task) => navigate(`/tasks/${task.id}`)}
        />
      ) : taskView === 'kanban' ? (
        <KanbanBoard
          tasks={tasks}
          onStatusChange={handleStatusChange}
          onTaskClick={(task) => navigate(`/tasks/${task.id}`)}
        />
      ) : (
        <div className="space-y-2">
          {tasks.map(task => (
            <div
              key={task.id}
              onClick={() => navigate(`/tasks/${task.id}`)}
              className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-colors ${
                darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700/80' : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <WorkStatusBadge status={task.status} clickable onCycle={(s) => handleStatusChange(task.id, s)} />
              <span className={`flex-1 font-medium text-sm ${task.status === 'completed' ? 'line-through text-gray-500' : ''}`}>{task.title}</span>
              <PriorityChip priority={task.priority} />
              <DueDateLabel date={task.due_date} />
            </div>
          ))}
          {tasks.length === 0 && (
            <p className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No tasks in this project</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
